
export type StateFieldName =
  | 'transcodingState'
  | 'transcriptionState'
  | 'personDetectionState'
  | 'celebrityDetectionState'
  | 'labelDetectionState'
  | 'textDetectionState'
  | 'archiveFileTransferState';

/*
  These fields are returned by the getItems API call if you don't specify which
  fields you want.

  This list should match the properties that prepareDto() in getItems.ts copies.
*/
export const standardItemEntryKeys = <const> [
  'id',
  'title',
  'personDetectionErrorMessage',
  'transcriptionErrorMessage',
  'celebrityDetectionErrorMessage',
  'labelDetectionErrorMessage',
  'textDetectionErrorMessage',
  'transcodingErrorMessage',
  'personDetectionState',
  'transcriptionState',
  'celebrityDetectionState',
  'labelDetectionState',
  'textDetectionState',
  'transcodingState',
  'createdOn',
  'modifiedOn',
  'itemState',
  'isDeleting',
  'folderParents',
  'media',
  'mediaType',
  'metadata',
  'metadataVersion',
  'progressInfo',
  'uploadProgressInfo',
  'warnings',
  'originalFileName',
  'hasEditProxy',
  'editProxyFormat',
  'hasProxy',
  'editProxyIngested',
  'itemType',
  'editProxyAvailable',
  'isArchived',
  'visibleTo',
  'createdBy',
  'updatableBy',
  'archiveFileTransferState',
  'archiveFileTransferErrorMessage',
  'ingestSourceFullPath',
  'ingest',
  'keldaIngest',
  'liveRecording',
  // Persisted liveness flag. A TAMS-referenced live item has no `media`, so
  // `media.streaming` cannot convey its live state; the search-hit poller and
  // live indicators read this to keep such an item live-refreshing until its
  // flow finalizes.
  'isLive',
  'technicalMetadata',
  'highresDeletion',
  'timecode',
  'mediaFrameRate',
  'taskEntries',
  'customIcons'
];

/*
  These additional fields can also be requested by explicitly telling the
  getItems API which fields you want.
*/
export const additionalSelectiveItemEntryKeys = <const> [
  'archiveInfo',
  'originalFileName',
  'mediaSize',
  'proxySize',
  'virtualClipInfo',
  'mediaDuration',
  'proxy',
  'rewrapCompatibilityHash',
  'indexLocation',
  'indexUrl',
  'mediaIssues',
];

/**
 * If the item update endpoint returns a 409 the response body
 * should include a value of this type to indicate the specific
 * type of failure.
 */
export enum ItemUpdateConflictReason {
  // The server item state did not match a precondition
  // sent by the client, such as the 'requiredMetadataVersion' condition.
  PreconditionFailed = 'PreconditionFailed'
}

export function updateItemConflictBody(reason: ItemUpdateConflictReason) {
  return {
    reason,
  };
}

export enum CommonProgressStatus {
  QUEUED_LIVE = 'Live ingest queued',
  QUEUED = 'Queued',
  ERROR = 'Error',
  COMPLETE = 'Done',
  RETRYING = 'Retrying'
}

// Defines the valid types of data that can be uploaded
// by a client to our generated-media bucket by requesting a signed url.
const generatedMediaUrlTypes = ['exif', 'thumbnail', 'aaf'] as const;
export type GeneratedMediaUrlType = typeof generatedMediaUrlTypes[number];

export function isGeneratedMediaUrlType(input: any): input is GeneratedMediaUrlType {
  return (generatedMediaUrlTypes).includes(input);
}

export type GeneratedMediaSignedUrlResponseDto = {
  signedUrl: string,

  // The expected content type for the file.
  // This is part of the signed url and so must be set by the client.
  contentType: string,
};

export type ScheduleHighresDeletionRequestDto = {
  deletionDate: string,
};

export const isHidden = (item: { isHidden?: boolean }) =>
  item.isHidden === true;

export const READABLE_MDF_FIELDS_QUERY_PARAMETER = 'readableMetadataFields';

/**
 * Changes to these properties makes an item 'dirty' i.e. outdated and indicates to clients that these properties should be refreshed
 */
export const DIRTY_ITEM_PROPS = (<const>['visibleTo', 'updatableBy', 'taskEntries']).concat();

/**
 * Item properties that are related to analytics operations
 */
export const itemAnalyticsProperties = <const>[
  // Transcription
  'transcriptionEnabled',
  'transcriptionChannels',
  'transcriptionService',
  'transcriptionChannelIdentification',
  'languageCode',

  // Label detection
  'labelDetectionEnabled',

  // Text detection
  'textDetectionEnabled',
  'textDetectionSettings',

  // Person detection
  'personDetectionEnabled',
  'createUnknownPersons',

  // Celebrity detection
  'celebrityDetectionEnabled',

  'analyticPlugins',
];
